import { PropertiesPanelContent, PropertiesPanelPropertyConfig } from '../../../../../widgets/propertiesPanel';
import { T } from 'src/shared/helpers';

export enum CameraProperty {
    CAMERA_POSITION_FOV,
    CAMERA_HEIGHT,
    CAMERA_LIMIT_ZOOM,
    CAMERA_ENABLE_LIMIT_MIN_ZOOM,
    CAMERA_ENABLE_LIMIT_MAX_ZOOM,
    CAMERA_LIMIT_MIN_ZOOM,
    CAMERA_LIMIT_MAX_ZOOM,
    CAMERA_LOCK_HORIZONTAL,
    CAMERA_LOCK_VERTICAL,
    CAMERA_MIN_ALPHA,
    CAMERA_MAX_ALPHA,
    CAMERA_MIN_BETA,
    CAMERA_MAX_BETA
}

export type CameraConfigHelpers = {
    onPropertyChange: (property: CameraProperty, value?: any) => void;
};

export type CameraNode = {
    id: string;
    name: string;
    position: [number, number, number];
    target: [number, number, number];
    fov: number;
    isActive: boolean;
    enableZoomLimit: boolean;
    enableHorizontalLimit: boolean;
    enableVerticalLimit: boolean;
    // Proprietà zoom
    useMinZoomPerc: boolean;
    useMaxZoomPerc: boolean;
    minZoomPerc: number;
    maxZoomPerc: number;
    // Proprietà rotazione
    lowerBetaLimits: number | undefined;
    upperBetaLimits: number | undefined;
    lowerAlphaLimit: number | undefined;
    upperAlphaLimit: number | undefined;
    height: number;
};

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

// Configurazione per il gruppo delle proprietà di base della camera
const createBasicCameraPropertiesGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'camera-properties',
        header: T._('Camera', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // FOV
            createPropertyConfig({
                type: 'range',
                id: 'fov',
                label: T._('FOV', 'Admin'),
                value: camera.fov,
                min: 0,
                max: 2,
                step: 0.1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_POSITION_FOV, value)
            }),

            // Height
            createPropertyConfig({
                type: 'range',
                id: 'height',
                label: T._('Height', 'Admin'),
                value: camera.height,
                min: 0,
                max: 10,
                step: 0.1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_HEIGHT, value)
            })
        ]
    });
};

// Configurazione per il gruppo dei limiti di zoom
const createZoomLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const properties: PropertiesPanelPropertyConfig[] = [
        // Abilita/disabilita zoom limit
        createPropertyConfig({
            type: 'switch',
            id: 'enable-zoom-limit',
            label: T._('Zoom limit', 'Admin'),
            checked: camera.enableZoomLimit,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_ZOOM, checked)
        })
    ];

    // Se zoom limit è abilitato, aggiungi i controlli
    if (camera.enableZoomLimit) {
        // Min zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-min-zoom',
                label: T._('Enable the minimum zoom limit', 'Admin'),
                checked: camera.useMinZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MIN_ZOOM, checked)
            })
        );

        if (camera.useMinZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'min-zoom-perc',
                    label: T._('Min', 'Admin'),
                    value: camera.minZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MIN_ZOOM, value / 100)
                })
            );
        }

        // Max zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-max-zoom',
                label: T._('Enable the maximum zoom limit', 'Admin'),
                checked: camera.useMaxZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MAX_ZOOM, checked)
            })
        );

        if (camera.useMaxZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'max-zoom-perc',
                    label: T._('Max', 'Admin'),
                    value: camera.maxZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MAX_ZOOM, value / 100)
                })
            );
        }
    }

    return createPropertyConfig({
        type: 'group',
        id: 'zoom-limits',
        header: T._('Zoom limits', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// Configurazione per il gruppo dei limiti di rotazione
const createRotationLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const isAlphaLimited = camera.upperAlphaLimit != null && camera.lowerAlphaLimit != null;
    const isBetaLimited = camera.upperBetaLimits != null && camera.lowerBetaLimits != null;

    const properties: PropertiesPanelPropertyConfig[] = [
        // Horizontal rotation limit
        createPropertyConfig({
            type: 'switch',
            id: 'horizontal-rotation-limit',
            label: T._('Horizontal rotation limit', 'Admin'),
            checked: isAlphaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_HORIZONTAL, checked)
        })
    ];

    if (isAlphaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-alpha',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_ALPHA, value * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-alpha',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_ALPHA, value * (Math.PI / 180))
            })
        );
    }

    // Vertical rotation limit
    properties.push(
        createPropertyConfig({
            type: 'switch',
            id: 'vertical-rotation-limit',
            label: T._('Vertical rotation limit', 'Admin'),
            checked: isBetaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_VERTICAL, checked)
        })
    );

    if (isBetaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-beta',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_BETA, (value + 90) * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-beta',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_BETA, (value + 90) * (Math.PI / 180))
            })
        );
    }

    return createPropertyConfig({
        type: 'group',
        id: 'rotation-limits',
        header: T._('Rotation limits', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// LA COLONNA HA DUE CAPITOLI. «Basic Properties» non diceva niente — sono le proprietà di base
// di che cosa? — e i due gruppi dei limiti stavano allo stesso livello di quello che descrive la
// camera, come se limitare uno zoom fosse una caratteristica dell'obiettivo.
//
// Il criterio è quello del mockup: sopra la camera come è fatta, sotto le regole che le si danno
// — i limiti sono impostazioni, non ottica.
const cameraPropertiesByType = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelContent[] => {
    return [
        createPropertyConfig({
            type: 'section',
            id: 'camera-scene',
            title: T._('Scene', 'Admin'),
            properties: [createBasicCameraPropertiesGroup(camera, helpers)]
        }),
        createPropertyConfig({
            type: 'section',
            id: 'camera-settings',
            title: T._('Settings', 'Admin'),
            properties: [createZoomLimitsGroup(camera, helpers), createRotationLimitsGroup(camera, helpers)]
        })
    ];
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useCameraPropertiesCreator = (): ((camera: CameraNode, helpers: CameraConfigHelpers) => PropertiesPanelContent[]) => {
    return (camera: CameraNode, helpers: CameraConfigHelpers) => {
        return cameraPropertiesByType(camera, helpers);
    };
};
